import React, { useContext, useEffect, useRef, useState } from "react";

import { useSelector } from "react-redux";
import PageContainer from "~/components/PageContainer";
import { FullGridContext } from "..";
import { format, parseISO } from "date-fns";
import PagePreview from "./Preview/index.js";
import { useSearchParams } from "react-router-dom";
import ModalFilters, { getStoredFilters } from "./ModalFilters";
import { Pin } from "lucide-react";
import api from "~/services/api";
import InactivateEnrollment from "./Inactivate";

const hasPinnedFilters = () => {
  const stored = getStoredFilters();
  return Boolean(
    stored &&
      Object.entries(stored).some(
        ([key, value]) => value && (key !== "showInactive" || value === "true")
      )
  );
};

export default function CommercialEnrollments() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [filtersPinned, setFiltersPinned] = useState(hasPinnedFilters);
  const [applyingFilters, setApplyingFilters] = useState(false);
  const [activeFilterQuery, setActiveFilterQuery] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const requestId = useRef(0);
  const [selected, setSelected] = useState([]);
  const [inactivateOpen, setInactivateOpen] = useState(false);

  const filial = useSelector((state) => state.auth.filial);
  const defaultOrderBy = { column: "application", asc: true };
  const defaultGridHeader = [
    {
      title: "Enroll. Start",
      name: "start_date",
      type: "text",
      filter: false,
      headerClassName: "w-20",
      cellClassName: "w-20",
    },
    {
      title: "Category",
      name: "category",
      type: "text",
      filter: true,
      headerClassName: "w-24",
      cellClassName: "w-24 overflow-hidden",
      cellTextClassName: "truncate block",
    },
    {
      title: "Name",
      name: "name",
      type: "text",
      filter: true,
      headerClassName: "w-36",
      cellClassName: "w-36 overflow-hidden",
      cellTextClassName: "truncate block",
    },
    {
      title: "Type",
      name: "type",
      type: "text",
      filter: true,
      headerClassName: "w-28",
      cellClassName: "w-28 overflow-hidden",
      cellTextClassName: "truncate block",
    },
    {
      title: "Sub Status",
      name: "sub_status",
      type: "text",
      filter: true,
      headerClassName: "w-28",
      cellClassName: "w-28 overflow-hidden",
      cellTextClassName: "truncate block",
    },
    {
      title: "Application",
      name: "application",
      type: "text",
      filter: true,
      headerClassName: "w-24",
      cellClassName: "w-24 overflow-hidden",
      cellTextClassName: "truncate block",
    },
    {
      title: "Phase Step",
      name: "phase_step",
      type: "text",
      filter: false,
      headerClassName: "w-28",
      cellClassName: "w-28 overflow-hidden",
      cellTextClassName: "truncate block",
    },
    {
      title: "Step Date",
      name: "step_date",
      type: "text",
      filter: false,
      headerClassName: "w-32",
      cellClassName: "w-32",
    },
    {
      title: "Step Status",
      name: "step_status",
      type: "text",
      filter: false,
      headerClassName: "w-24",
      cellClassName: "w-24 overflow-hidden",
      cellTextClassName: "truncate block",
    },
    {
      title: "Expected Date",
      name: "expected_date",
      type: "text",
      filter: false,
      headerClassName: "w-24",
      cellClassName: "w-24",
    },
    {
      title: "Agent",
      name: "agent",
      type: "text",
      filter: true,
      headerClassName: "w-20",
      cellClassName: "w-20 overflow-hidden",
      cellTextClassName: "truncate block",
    },
    {
      title: "Status",
      name: "status",
      type: "text",
      filter: false,
      headerClassName: "w-24",
      cellClassName: "w-24 overflow-hidden",
      cellTextClassName: "truncate block",
    },
  ];
  const {
    accessModule,
    activeFilters,
    gridData,
    gridDetails,
    gridHeader,
    handleFilters,
    handleOpened,
    limit,
    loadingData,
    opened,
    orderBy,
    page,
    pages,
    search,
    setActiveFilters,
    setGridData,
    setGridDetails,
    setGridHeader,
    setLimit,
    setLoadingData,
    setOpened,
    setOrderBy,
    setPage,
    setPages,
    setSearch,
    setTotalRows,
    setSuccessfullyUpdated,
    successfullyUpdated,
    totalRows,
  } = useContext(FullGridContext);

  const pageAccess = accessModule.children.find(
    (el) => el.alias === "enrollments"
  );
  const mapEnrollments = (data) =>
    data.map(
      (
        {
          id,
          students,
          enrollmenttimelines,
          canceled_at,
          application,
          contract,
          agents,
          status,
        },
        index
      ) => {
        const { name, last_name, category } = students;
        const { processtypes, processsubstatuses } = contract || {};
        const { name: agent } = agents || {};
        const type = processtypes ? processtypes.name : "";
        const sub_status = processsubstatuses ? processsubstatuses.name : "";
        const latestTimeline =
          enrollmenttimelines[enrollmenttimelines.length - 1] || {};
        const {
          phase_step,
          created_at: stepCreatedAt,
          step_status,
          expected_date,
        } = latestTimeline;
        const enroll_start =
          enrollmenttimelines.length > 0
            ? format(parseISO(enrollmenttimelines[0].created_at), "MM/dd/yyyy")
            : "-";
        const isInactive = status === "Inactive";
        const ret = {
          show: true,
          id,
          fields: [
            enroll_start,
            category,
            name + " " + last_name,
            type,
            sub_status,
            application,
            phase_step || "",
            stepCreatedAt
              ? format(parseISO(stepCreatedAt), "MM/dd/yyyy @ HH:mm")
              : "",
            step_status || "",
            expected_date ? format(parseISO(expected_date), "MM/dd/yyyy") : "",
            agent,
            status || "",
          ],
          selectable:
            !isInactive &&
            !canceled_at &&
            (category === "Prospect" || category === "Ex-student"),
          canceled: canceled_at || isInactive,
          page: Math.ceil((index + 1) / limit),
        };
        return ret;
      }
    );
  async function loader() {
    const currentRequest = ++requestId.current;
    setLoadingData(true);
    const params = new URLSearchParams({
      limit: String(limit),
      page: String(page),
      orderBy: orderBy?.column || defaultOrderBy.column,
      orderASC: (orderBy?.asc ?? defaultOrderBy.asc) ? "ASC" : "DESC",
      search: search?.value || "",
    });
    if (activeFilterQuery) {
      const applied = new URL(activeFilterQuery, window.location.origin).searchParams;
      [
        "category",
        "name",
        "type",
        "substatus",
        "application",
        "agent",
        "phase_step",
        "step_status",
        "showInactive",
      ].forEach((key) => {
        const value = applied.get(key);
        if (value && value !== "null") params.set(key, value);
      });
    } else {
      const stored = getStoredFilters();
      if (stored) {
        const filterKeys = {
          category: "category",
          name: "name",
          type: "type",
          subStatus: "substatus",
          application: "application",
          agent: "agent",
          phaseStep: "phase_step",
          stepStatus: "step_status",
          showInactive: "showInactive",
        };
        Object.entries(filterKeys).forEach(([field, key]) => {
          if (
            stored[field] &&
            (key !== "showInactive" || stored[field] === "true")
          ) {
            params.set(key, stored[field]);
          }
        });
      }
    }
    try {
      const { data } = await api.get(`/enrollments?${params.toString()}`);
      if (currentRequest !== requestId.current) return;
      const pagesCalc = Math.ceil((data?.totalRows || 0) / limit) || 1;
      setPages(pagesCalc);
      setGridDetails({ totalRows: data?.totalRows || 0, pages: pagesCalc });
      setGridData(mapEnrollments(data?.rows || []));
    } catch (err) {
      if (currentRequest === requestId.current) console.log(err);
    } finally {
      if (currentRequest === requestId.current) {
        setApplyingFilters(false);
        setLoadingData(false);
      }
    }
  }

  const applyFiltersToGrid = (queryString) => {
    setApplyingFilters(true);
    setActiveFilters([]);
    setPage(1);
    setActiveFilterQuery(queryString);
    setRefreshKey((current) => current + 1);
  };

  useEffect(() => {
    setActiveFilters([]);
    setSearch("");
  }, []);

  useEffect(() => {
    loader();
    if (searchParams?.has("id")) {
      setSearchParams("");
      setOpened(searchParams?.get("id"));
    }
    return () => {
      requestId.current += 1;
    };
  }, [opened, filial, search, limit, page, orderBy, activeFilterQuery, refreshKey]);

  const clearFilters = () => {
    setActiveFilterQuery(null);
    setPage(1);
    setRefreshKey((current) => current + 1);
  };

  const handleInactivate = () => {
    const newVarOpened = !inactivateOpen;
    setInactivateOpen(newVarOpened);
    if (!newVarOpened) {
      setSelected([]);
      loader();
    }
    handleOpened(null);
  };

  const selectionFunctions = [];
  if (selected.length > 0) {
    selectionFunctions.push({
      title: "Inactivate Enrollment",
      alias: "inactivate",
      fun: handleInactivate,
      icon: "Ban",
      Page: InactivateEnrollment,
      opened: inactivateOpen,
      setOpened: setInactivateOpen,
      selected,
    });
  }

  return (
    <>
      <ModalFilters
        opened={filtersModalOpen}
        applying={applyingFilters}
        onClose={() => {
          setFiltersModalOpen(false);
          setFiltersPinned(hasPinnedFilters());
        }}
        onApplyFilters={applyFiltersToGrid}
        onClearFilters={clearFilters}
      />
      <PageContainer
        handleNew={false}
        FullGridContext={FullGridContext}
        PagePreview={PagePreview}
        pageAccess={pageAccess}
        defaultGridHeader={defaultGridHeader}
        containerClassName="min-w-0 w-full"
        gridHorizontalScroll={true}
        gridMinWidthClassName="min-w-[1200px] w-full"
        selection={{
          multiple: true,
          selected,
          setSelected,
          functions: selectionFunctions,
        }}
        filtersModal={{
          onOpen: () => setFiltersModalOpen(true),
          viewSelect: filtersPinned ? (
            <div className="flex items-center gap-1 text-orange-500 text-xs font-medium">
              <Pin size={13} className="fill-orange-500" />
              Filters Applied
            </div>
          ) : null,
        }}
      />
    </>
  );
}
